/**
 * CoverLettersHistory.jsx — RC 1.41 Phase C
 *
 * Read-only history list of cover letters for the Documents library.
 * No generation UI — generation happens exclusively inside the Job
 * Application Wizard (Phase D). Users can:
 *   - browse the list
 *   - preview content in a read-only modal
 *   - delete an unused letter (operational; doesn't violate the
 *     RC 1.41 immutability rule, which is about *pack documents*)
 *   - export
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Mail, Loader2, Trash2, ExternalLink, Lock, Sparkles,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cachedFetch, peek, invalidate, KEYS } from '@/lib/listCache';
import { toast } from 'sonner';
import DeleteDocumentDialog from '@/components/DeleteDocumentDialog';
import ExportButton from '@/components/ExportButton';
import CvRenderer from '@/components/render/CvRenderer';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

function PreviewModal({ open, onOpenChange, id, title, subtitle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !id) return;
    let alive = true;
    setLoading(true); setError(''); setData(null);
    apiFetch(`/api/cover-letters/${id}`)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.message || 'Failed to load.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        data-testid="letter-preview-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            <Badge variant="outline" className="text-[10px] tracking-[0.16em] uppercase border-ndor-edge text-ndor-ink-muted">
              <Lock className="h-2.5 w-2.5 mr-1" />
              Read-only
            </Badge>
          </DialogTitle>
          {subtitle && <p className="text-xs text-ndor-ink-muted">{subtitle}</p>}
        </DialogHeader>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted mx-auto my-6" />}
        {error && <p className="text-sm text-ndor-error">{error}</p>}
        {!loading && !error && (
          <div data-testid="letter-preview-body">
            {data
              ? <CvRenderer mode="letter" data={data} />
              : <span className="text-ndor-ink-muted italic text-sm">No content available.</span>
            }
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// RC 1.59 Slice 2 — Documents tab cache adoption (Tier 1).
const LETTERS_TTL_MS = 5 * 60_000;

export default function CoverLettersHistory() {
  const [items, setItems] = useState(() => {
    const seed = peek(KEYS.coverLettersAll(), LETTERS_TTL_MS);
    return seed.hit && Array.isArray(seed.data) ? seed.data : [];
  });
  const [loading, setLoading] = useState(() => {
    const seed = peek(KEYS.coverLettersAll(), LETTERS_TTL_MS);
    return !seed.hit;
  });
  const [previewId, setPreviewId] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewSubtitle, setPreviewSubtitle] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const list = await cachedFetch(
        KEYS.coverLettersAll(),
        () => apiFetch('/api/cover-letters'),
        { ttlMs: LETTERS_TTL_MS },
      );
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load cover letters.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cover-letters-history">
      {items.length === 0 ? (
        <Card className="border-ndor-edge shadow-sm">
          <CardContent className="p-8 text-center space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-ndor-surface-alt flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-ndor-ink-muted" />
            </div>
            <p className="text-sm font-medium text-ndor-ink">No cover letters yet</p>
            <p className="text-xs text-ndor-ink-muted leading-relaxed max-w-sm mx-auto">
              Cover letters are generated when you apply to a job. Open a job to start.
            </p>
            <Button asChild variant="outline" size="sm" data-testid="letters-history-jobs-link">
              <Link to="/jobs"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Browse jobs</Link>
            </Button>

            {/* RC 1.49.x Slice 8 — calm reviewer-guidance hint mirroring
                Slice 7. Sequencing only — not a walkthrough or product
                tour. Renders only on the genuine empty state, so
                returning-user UX is untouched. */}
            <div
              className="mt-5 mx-auto max-w-md rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-left"
              data-testid="letters-empty-reviewer-hint"
            >
              <p className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
                Recommended next step
              </p>
              <ol className="mt-2 space-y-1.5 text-xs text-ndor-ink-secondary leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="font-medium text-ndor-ink">1.</span>
                  <span><Link to="/jobs" className="text-ndor-info hover:underline">Import or choose a job</Link> from the Jobs page.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-ndor-ink">2.</span>
                  <span>Run <span className="font-medium text-ndor-ink">Analyse Fit</span> and generate a tailored CV for the role.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-ndor-ink">3.</span>
                  <span>Inside the application flow, generate a <span className="font-medium text-ndor-ink">cover letter</span> — it lands here in your library.</span>
                </li>
              </ol>
            </div>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((l) => (
            <li
              key={l.id}
              /* RC 1.59 Slice 4 Mobile pass — stack on phones; resume
                 horizontal layout at sm: (≥640 px). */
              className="rounded-lg border border-ndor-edge bg-ndor-surface px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3"
              data-testid={`letter-row-${l.id}`}
            >
              <div className="flex items-start gap-3 sm:contents min-w-0 w-full">
                <div className="w-9 h-9 rounded-lg bg-ndor-surface-alt flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-ndor-ink-muted" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ndor-ink truncate" data-testid="letter-row-title">
                    {l.title || 'Untitled cover letter'}
                  </p>
                  <p className="text-xs text-ndor-ink-muted line-clamp-2 sm:line-clamp-none break-words">
                    {l.target_role || 'No target role'} · Generated {fmtDate(l.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:shrink-0 self-end sm:self-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPreviewId(l.id);
                    setPreviewTitle(l.title || 'Cover letter');
                    setPreviewSubtitle(`Generated ${fmtDate(l.created_at)}`);
                  }}
                  data-testid="letter-row-preview"
                >
                  Preview
                </Button>
                <ExportButton letterId={l.id} title={l.title || 'cover-letter'} />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-ndor-ink-muted hover:text-ndor-error"
                  onClick={() => setDeleteTarget(l)}
                  data-testid="letter-row-delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-[11px] text-ndor-ink-muted leading-relaxed pt-1">
        To create a new cover letter, open a job from{' '}
        <Link to="/jobs" className="underline hover:text-ndor-ink">/jobs</Link> and use
        the application flow.
      </p>

      <PreviewModal
        open={!!previewId}
        onOpenChange={(v) => !v && setPreviewId(null)}
        id={previewId}
        title={previewTitle}
        subtitle={previewSubtitle}
      />
      <DeleteDocumentDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        kind="cover_letter"
        doc={deleteTarget}
        onDeleted={() => {
          invalidate(KEYS.coverLettersAll());
          setDeleteTarget(null);
          refresh();
        }}
      />
    </div>
  );
}
